// src/components/admin/PageManagement.js - Updated with hash navigation support
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLocation } from "react-router-dom";

// Import components
import GeneralTab from "./page-management/GeneralTab";
import SectionsTab from "./page-management/SectionsTab";
import SeoTab from "./page-management/SeoTab";
import LoadingSpinner from "../LoadingSpinner";

// Import lucide icons for a consistent look
import {
  Save,
  Eye,
  Trash2,
  PlusCircle,
  AlertCircle,
  FileText,
  Layers,
  Search,
  ArrowLeft,
  ChevronRight,
} from "lucide-react";

const PageManagement = ({ setActiveMenu }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [showForm, setShowForm] = useState(false);
  const [currentPageId, setCurrentPageId] = useState(null);
  const [hashTarget, setHashTarget] = useState(null); // New state for hash navigation

  // Form state for general page details
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    isPublished: false,
    template: "default",
    metaTitle: "",
    metaDescription: "",
  });

  // Handle hash navigation from live edit mode
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      setHashTarget(hash);

      // If hash indicates section editing, switch to sections tab
      if (hash.includes("#section-") || hash.includes("#add-section")) {
        setActiveTab("sections");
      }
      // If hash indicates section editing, switch to sections tab
      if (hash.includes("#page")) {
        setActiveTab("general");
      }
    }
  }, [location.hash]);

  // Pass hash target to SectionsTab when page is loaded
  useEffect(() => {
    if (currentPage && hashTarget) {
      // This will be handled by SectionsTab
      console.log("Hash target set:", hashTarget);
    }
  }, [currentPage, hashTarget]);

  // Fetch all pages for the sidebar
  const fetchPages = useCallback(async () => {
    try {
      const response = await axios.get("/api/pages");
      setPages(response.data);
    } catch (err) {
      console.error("Error fetching pages:", err);
      setError("Failed to load pages. Please try again.");
    }
  }, []);

  // Fetch a specific page by ID
  const fetchPage = useCallback(async (pageId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/admin/pages/${pageId}`);

      setCurrentPage(response.data);
      setCurrentPageId(pageId);
      setFormData({
        title: response.data.title || "",
        slug: response.data.slug || "",
        description: response.data.description || "",
        isPublished: response.data.isPublished || false,
        template: response.data.template || "default",
        metaTitle: response.data.metaTitle || "",
        metaDescription: response.data.metaDescription || "",
      });
      setError(null);
      setShowForm(true);
    } catch (err) {
      console.error("Error fetching page:", err);
      setError("Failed to load page details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Extract page ID from URL if present
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/pitech\/pages\/([a-zA-Z0-9]+)$/);
    if (match && match[1]) {
      fetchPage(match[1]);
    } else {
      setCurrentPage(null);
      setLoading(false);
      setShowForm(false);
    }
  }, [fetchPage]);

  // Load data on component mount
  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // Handle input changes for general form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Generate slug from title
  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .replace(/^-+/, "")
      .replace(/-+$/, "");

    setFormData({
      ...formData,
      slug,
    });
  };

  // Save/update page
  const savePage = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      const pageData = {
        ...formData,
      };

      let response;

      if (currentPage) {
        // Update existing page
        response = await axios.put(
          `/api/admin/pages/${currentPage._id}`,
          pageData
        );
        setCurrentPage(response.data);
        setSuccess("Page updated successfully!");
      } else {
        // Create new page
        response = await axios.post("/api/admin/pages", pageData);
        setCurrentPage(response.data);
        setCurrentPageId(response.data._id);
        // Update URL without navigating away from dashboard
        window.history.pushState({}, "", `/pitech/pages/${response.data._id}`);
        setSuccess("Page created successfully!");
      }

      await fetchPages(); // Refresh sidebar
      setError(null);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error("Error saving page:", err);
      setError("Failed to save page. Please check the form and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Delete page
  const deletePage = async () => {
    if (!currentPage) return;

    if (
      !window.confirm(
        "Are you sure you want to delete this page? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`/api/admin/pages/${currentPage._id}`);
      await fetchPages();
      window.history.pushState({}, "", "/pitech/pages");
      setCurrentPage(null);
      setCurrentPageId(null);
      setError(null);
      setSuccess("Page deleted successfully!");
      setShowForm(false);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error("Error deleting page:", err);
      setError("Failed to delete page. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Preview page
  const previewPage = () => {
    if (!currentPage) return;

    // Open preview in new tab
    window.open(`/${currentPage.slug}`, "_blank");
  };

  // Clear notification messages
  const clearNotifications = () => {
    setError(null);
    setSuccess(null);
  };

  // Create a new page
  const handleNewPage = () => {
    setCurrentPage(null);
    setCurrentPageId(null);
    setHashTarget(null); // Clear hash target
    window.history.pushState({}, "", "/pitech/pages");
    setFormData({
      title: "",
      slug: "",
      description: "",
      isPublished: false,
      template: "default",
      metaTitle: "",
      metaDescription: "",
    });
    setActiveTab("general");
    setShowForm(true);
  };

  // Back to page list
  const handleBackToList = () => {
    window.history.pushState({}, "", "/pitech/pages");
    setCurrentPageId(null);
    setHashTarget(null); // Clear hash target
    setShowForm(false);
  };

  // Handle clicking on a page to edit
  const handlePageClick = (pageId) => {
    window.history.pushState({}, "", `/pitech/pages/${pageId}`);
    setHashTarget(null); // Clear hash target when manually navigating
    fetchPage(pageId);
  };

  // Handle background settings update
  const handleBackgroundUpdate = (updatedData) => {
    setFormData({
      ...formData,
      backgroundType: updatedData.backgroundType,
      backgroundColor: updatedData.backgroundColor,
      backgroundImage: updatedData.backgroundImage,
      backgroundSize: updatedData.backgroundSize,
      backgroundPosition: updatedData.backgroundPosition,
      backgroundRepeat: updatedData.backgroundRepeat,
      backgroundAttachment: updatedData.backgroundAttachment,
      backgroundVideo: updatedData.backgroundVideo,
      videoLoop: updatedData.videoLoop,
      videoMuted: updatedData.videoMuted,
      videoAutoplay: updatedData.videoAutoplay,
      backgroundOverlay: updatedData.backgroundOverlay,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <h2 className="text-2xl font-semibold">Page Management</h2>
      <div className="p-1 bg-gray-50 order border-gray-200 rounded-2xl">
        {/* Notification area - always visible */}
        <div className="container mx-auto px-4 py-2">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex justify-between items-center">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <p>{error}</p>
              </div>
              <button
                onClick={clearNotifications}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex justify-between items-center">
              <p>{success}</p>
              <button
                onClick={clearNotifications}
                className="text-green-500 hover:text-green-700"
              >
                ×
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="h-64 flex justify-center items-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {!showForm ? (
              /* Page List View */
              <div className="container mx-auto">
                <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h1 className="text-2xl font-semibold text-gray-800">
                        Existing Pages
                      </h1>
                      <button
                        onClick={handleNewPage}
                        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors"
                      >
                        <PlusCircle className="w-4 h-4 mr-2" /> Create New Page
                      </button>
                    </div>
                  </div>

                  {pages.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
                        <FileText className="w-8 h-8 text-blue-500" />
                      </div>
                      <h2 className="text-xl font-medium text-gray-800 mb-2">
                        No Pages Found
                      </h2>
                      <p className="text-gray-600 mb-6">
                        Create your first page to get started.
                      </p>
                      <button
                        onClick={handleNewPage}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors mx-auto"
                      >
                        <PlusCircle className="w-4 h-4 mr-2" /> Create New Page
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {pages.map((page) => (
                        <div
                          key={page._id}
                          className="p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="sm:flex justify-between items-center">
                            <div className="mb-2 sm:mb-0">
                              <h3 className="font-medium text-gray-900">
                                {page.title}
                              </h3>
                              <div className="text-sm text-gray-500 flex flex-wrap items-center mt-1">
                                <span className="mr-3">/{page.slug}</span>
                                {!page.isPublished && (
                                  <span className="bg-gray-200 text-gray-800 px-2 py-0.5 rounded text-xs font-medium">
                                    Draft
                                  </span>
                                )}
                                {page.isPublished && (
                                  <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium">
                                    Published
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handlePageClick(page._id)}
                              style={{ transform: "none", boxShadow: "none" }}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                            >
                              Edit <ChevronRight className="w-4 h-4 ml-1" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Page Form View */
              <div className="container mx-auto">
                <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                      <div className="flex items-center">
                        <button
                          style={{ transform: "none", boxShadow: "none" }}
                          onClick={handleBackToList}
                          className="mr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                        >
                          <ArrowLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-2xl font-semibold text-gray-800">
                          {currentPage
                            ? `Edit: ${currentPage.title}`
                            : "Create New Page"}
                        </h1>
                      </div>
                      <div className="flex space-x-2 w-full sm:w-auto justify-end">
                        {currentPage && (
                          <>
                            <button
                              onClick={previewPage}
                              className="bg-gray-100 font-normal hover:bg-gray-200 text-gray-800 px-3 py-1 rounded inline-flex items-center"
                            >
                              <Eye className="w-4 h-4 mr-1" /> Preview
                            </button>
                            <button
                              onClick={deletePage}
                              className="bg-red-500 font-normal hover:bg-red-600 text-white px-3 py-1 rounded inline-flex items-center"
                            >
                              <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </button>
                          </>
                        )}
                        <button
                          onClick={savePage}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
                        >
                          <Save className="w-4 h-4 mr-1" /> Save
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="px-6 bg-white border-b border-gray-200 overflow-x-auto">
                    <div className="flex whitespace-nowrap">
                      <button
                        className={`py-4 px-4 font-medium text-sm focus:outline-none transition-colors relative
                        ${
                          activeTab === "general"
                            ? "text-blue-600 border-b-2 border-blue-600"
                            : "text-gray-600 hover:text-gray-900"
                        }
                      `}
                        onClick={() => setActiveTab("general")}
                      >
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-2" />
                          General
                        </div>
                      </button>
                      <button
                        className={`py-4 px-4 font-medium text-sm focus:outline-none transition-colors relative
                        ${
                          activeTab === "sections"
                            ? "text-blue-600 border-b-2 border-blue-600"
                            : "text-gray-600 hover:text-gray-900"
                        }
                      `}
                        onClick={() => setActiveTab("sections")}
                        disabled={!currentPage}
                      >
                        <div className="flex items-center">
                          <Layers className="w-4 h-4 mr-3" />
                          Sections
                          {!currentPage && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                              Save first
                            </span>
                          )}
                        </div>
                      </button>
                      <button
                        className={`py-4 px-4 font-medium text-sm focus:outline-none transition-colors relative
                        ${
                          activeTab === "seo"
                            ? "text-blue-600 border-b-2 border-blue-600"
                            : "text-gray-600 hover:text-gray-900"
                        }
                      `}
                        onClick={() => setActiveTab("seo")}
                      >
                        <div className="flex items-center">
                          <Search className="w-4 h-4 mr-2" />
                          SEO
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-6">
                    {activeTab === "general" && (
                      <GeneralTab
                        formData={formData}
                        handleInputChange={handleInputChange}
                        generateSlug={generateSlug}
                        onSubmit={savePage}
                        handleBackgroundUpdate={handleBackgroundUpdate}
                      />
                    )}

                    {activeTab === "sections" && currentPage && (
                      <SectionsTab
                        page={currentPage}
                        setPage={setCurrentPage}
                        setError={setError}
                        setLoading={setLoading}
                        hashTarget={hashTarget} // Pass hash target to SectionsTab
                        onHashHandled={() => setHashTarget(null)} // Clear hash after handling
                      />
                    )}

                    {activeTab === "seo" && (
                      <SeoTab
                        formData={formData}
                        handleInputChange={handleInputChange}
                        onSubmit={savePage}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .btn-primary {
          @apply bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors;
        }
        
        .btn-secondary {
          @apply bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors;
        }
        
        .btn-danger {
          @apply bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors;
        }
        
        input[type="text"], 
        input[type="email"],
        input[type="password"],
        textarea,
        select {
          @apply w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all;
        }
        
        input[type="checkbox"] {
          @apply rounded text-blue-600 focus:ring-blue-500 h-4 w-4 transition-colors;
        }
        
        label {
          @apply text-sm font-medium text-gray-700 mb-1 block;
        }
      `}</style>
    </div>
  );
};

export default PageManagement;

// // before live edit
// // src/components/admin/PageManagement.js
// import React, { useState, useEffect, useCallback } from "react";
// import axios from "axios";
// import { useAuth } from "../../context/AuthContext";

// // Import components
// import GeneralTab from "./page-management/GeneralTab";
// import SectionsTab from "./page-management/SectionsTab";
// import SeoTab from "./page-management/SeoTab";
// import LoadingSpinner from "../LoadingSpinner";

// // Import lucide icons for a consistent look
// import {
//   Save,
//   Eye,
//   Trash2,
//   PlusCircle,
//   AlertCircle,
//   FileText,
//   Layers,
//   Search,
//   ArrowLeft,
//   ChevronRight,
// } from "lucide-react";

// const PageManagement = ({ setActiveMenu }) => {
//   const { user } = useAuth();
//   const [loading, setLoading] = useState(true);
//   const [pages, setPages] = useState([]);
//   const [currentPage, setCurrentPage] = useState(null);
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);
//   const [activeTab, setActiveTab] = useState("general"); // 'general', 'sections', 'seo'
//   const [showForm, setShowForm] = useState(false); // Toggle between pages list and form
//   const [currentPageId, setCurrentPageId] = useState(null);

//   // Form state for general page details
//   const [formData, setFormData] = useState({
//     title: "",
//     slug: "",
//     description: "",
//     isPublished: false,
//     template: "default",
//     metaTitle: "",
//     metaDescription: "",
//   });

//   // Fetch all pages for the sidebar
//   const fetchPages = useCallback(async () => {
//     try {
//       const response = await axios.get("/api/pages");
//       setPages(response.data);
//     } catch (err) {
//       console.error("Error fetching pages:", err);
//       setError("Failed to load pages. Please try again.");
//     }
//   }, []);

//   // Fetch a specific page by ID
//   const fetchPage = useCallback(async (pageId) => {
//     try {
//       setLoading(true);
//       const response = await axios.get(`/api/admin/pages/${pageId}`);

//       setCurrentPage(response.data);
//       setCurrentPageId(pageId);
//       setFormData({
//         title: response.data.title || "",
//         slug: response.data.slug || "",
//         description: response.data.description || "",
//         isPublished: response.data.isPublished || false,
//         template: response.data.template || "default",
//         metaTitle: response.data.metaTitle || "",
//         metaDescription: response.data.metaDescription || "",
//       });
//       setError(null);
//       setShowForm(true); // Show form when a page is fetched
//     } catch (err) {
//       console.error("Error fetching page:", err);
//       setError("Failed to load page details. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   // Extract page ID from URL if present
//   useEffect(() => {
//     const path = window.location.pathname;
//     const match = path.match(/\/pitech\/pages\/([a-zA-Z0-9]+)$/);
//     if (match && match[1]) {
//       fetchPage(match[1]);
//     } else {
//       setCurrentPage(null);
//       setLoading(false);
//       setShowForm(false); // Show page list when no ID is present
//     }
//   }, [fetchPage]);

//   // Load data on component mount
//   useEffect(() => {
//     fetchPages();
//   }, [fetchPages]);

//   // Handle input changes for general form
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === "checkbox" ? checked : value,
//     });
//   };

//   // Generate slug from title
//   const generateSlug = () => {
//     const slug = formData.title
//       .toLowerCase()
//       .replace(/[^\w\s-]/g, "")
//       .replace(/\s+/g, "-")
//       .replace(/--+/g, "-")
//       .replace(/^-+/, "")
//       .replace(/-+$/, "");

//     setFormData({
//       ...formData,
//       slug,
//     });
//   };

//   // Save/update page
//   const savePage = async (e) => {
//     e.preventDefault();
//     try {
//       setLoading(true);

//       const pageData = {
//         ...formData,
//       };

//       let response;

//       if (currentPage) {
//         // Update existing page
//         response = await axios.put(
//           `/api/admin/pages/${currentPage._id}`,
//           pageData
//         );
//         setCurrentPage(response.data);
//         setSuccess("Page updated successfully!");
//       } else {
//         // Create new page
//         response = await axios.post("/api/admin/pages", pageData);
//         setCurrentPage(response.data);
//         setCurrentPageId(response.data._id);
//         // Update URL without navigating away from dashboard
//         window.history.pushState({}, "", `/pitech/pages/${response.data._id}`);
//         setSuccess("Page created successfully!");
//       }

//       await fetchPages(); // Refresh sidebar
//       setError(null);

//       // Clear success message after 3 seconds
//       setTimeout(() => {
//         setSuccess(null);
//       }, 3000);
//     } catch (err) {
//       console.error("Error saving page:", err);
//       setError("Failed to save page. Please check the form and try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Delete page
//   const deletePage = async () => {
//     if (!currentPage) return;

//     if (
//       !window.confirm(
//         "Are you sure you want to delete this page? This action cannot be undone."
//       )
//     ) {
//       return;
//     }

//     try {
//       setLoading(true);
//       await axios.delete(`/api/admin/pages/${currentPage._id}`);
//       await fetchPages();
//       window.history.pushState({}, "", "/pitech/pages");
//       setCurrentPage(null);
//       setCurrentPageId(null);
//       setError(null);
//       setSuccess("Page deleted successfully!");
//       setShowForm(false); // Return to list view after deletion

//       // Clear success message after 3 seconds
//       setTimeout(() => {
//         setSuccess(null);
//       }, 3000);
//     } catch (err) {
//       console.error("Error deleting page:", err);
//       setError("Failed to delete page. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Preview page
//   const previewPage = () => {
//     if (!currentPage) return;

//     // Open preview in new tab
//     window.open(`/${currentPage.slug}`, "_blank");
//   };

//   // Clear notification messages
//   const clearNotifications = () => {
//     setError(null);
//     setSuccess(null);
//   };

//   // Create a new page
//   const handleNewPage = () => {
//     setCurrentPage(null);
//     setCurrentPageId(null);
//     window.history.pushState({}, "", "/pitech/pages");
//     setFormData({
//       title: "",
//       slug: "",
//       description: "",
//       isPublished: false,
//       template: "default",
//       metaTitle: "",
//       metaDescription: "",
//     });
//     setActiveTab("general");
//     setShowForm(true);
//   };

//   // Back to page list
//   const handleBackToList = () => {
//     window.history.pushState({}, "", "/pitech/pages");
//     setCurrentPageId(null);
//     setShowForm(false);
//   };

//   // Handle clicking on a page to edit
//   const handlePageClick = (pageId) => {
//     window.history.pushState({}, "", `/pitech/pages/${pageId}`);
//     fetchPage(pageId);
//   };

//   // Handle background settings update
//   const handleBackgroundUpdate = (updatedData) => {
//     setFormData({
//       ...formData,
//       backgroundType: updatedData.backgroundType,
//       backgroundColor: updatedData.backgroundColor,
//       backgroundImage: updatedData.backgroundImage,
//       backgroundSize: updatedData.backgroundSize,
//       backgroundPosition: updatedData.backgroundPosition,
//       backgroundRepeat: updatedData.backgroundRepeat,
//       backgroundAttachment: updatedData.backgroundAttachment,
//       backgroundVideo: updatedData.backgroundVideo,
//       videoLoop: updatedData.videoLoop,
//       videoMuted: updatedData.videoMuted,
//       videoAutoplay: updatedData.videoAutoplay,
//       backgroundOverlay: updatedData.backgroundOverlay,
//     });
//   };

//   return (
//     <div className="space-y-6">
//       {/* Header */}
//       <h2 className="text-2xl font-semibold">Page Management</h2>
//       <div className="p-1 bg-gray-50 order border-gray-200 rounded-2xl">
//         {/* Notification area - always visible */}
//         <div className="container mx-auto px-4 py-2">
//           {error && (
//             <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex justify-between items-center">
//               <div className="flex items-center">
//                 <AlertCircle className="w-5 h-5 mr-2" />
//                 <p>{error}</p>
//               </div>
//               <button
//                 onClick={clearNotifications}
//                 className="text-red-500 hover:text-red-700"
//               >
//                 ×
//               </button>
//             </div>
//           )}

//           {success && (
//             <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex justify-between items-center">
//               <p>{success}</p>
//               <button
//                 onClick={clearNotifications}
//                 className="text-green-500 hover:text-green-700"
//               >
//                 ×
//               </button>
//             </div>
//           )}
//         </div>

//         {loading ? (
//           <div className="h-64 flex justify-center items-center">
//             <LoadingSpinner />
//           </div>
//         ) : (
//           <>
//             {!showForm ? (
//               /* Page List View */
//               <div className="container mx-auto">
//                 <div className="bg-white shadow-sm rounded-lg overflow-hidden">
//                   <div className="p-6 border-b border-gray-200">
//                     <div className="flex justify-between items-center">
//                       <h1 className="text-2xl font-semibold text-gray-800">
//                         Existing Pages
//                       </h1>
//                       <button
//                         onClick={handleNewPage}
//                         className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors"
//                       >
//                         <PlusCircle className="w-4 h-4 mr-2" /> Create New Page
//                       </button>
//                     </div>
//                   </div>

//                   {pages.length === 0 ? (
//                     <div className="p-8 text-center">
//                       <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
//                         <FileText className="w-8 h-8 text-blue-500" />
//                       </div>
//                       <h2 className="text-xl font-medium text-gray-800 mb-2">
//                         No Pages Found
//                       </h2>
//                       <p className="text-gray-600 mb-6">
//                         Create your first page to get started.
//                       </p>
//                       <button
//                         onClick={handleNewPage}
//                         className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors mx-auto"
//                       >
//                         <PlusCircle className="w-4 h-4 mr-2" /> Create New Page
//                       </button>
//                     </div>
//                   ) : (
//                     <div className="divide-y divide-gray-200">
//                       {pages.map((page) => (
//                         <div
//                           key={page._id}
//                           className="p-4 hover:bg-gray-50 transition-colors"
//                         >
//                           <div className="sm:flex justify-between items-center">
//                             <div className="mb-2 sm:mb-0">
//                               <h3 className="font-medium text-gray-900">
//                                 {page.title}
//                               </h3>
//                               <div className="text-sm text-gray-500 flex flex-wrap items-center mt-1">
//                                 <span className="mr-3">/{page.slug}</span>
//                                 {!page.isPublished && (
//                                   <span className="bg-gray-200 text-gray-800 px-2 py-0.5 rounded text-xs font-medium">
//                                     Draft
//                                   </span>
//                                 )}
//                                 {page.isPublished && (
//                                   <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium">
//                                     Published
//                                   </span>
//                                 )}
//                               </div>
//                             </div>
//                             <button
//                               onClick={() => handlePageClick(page._id)}
//                               style={{ transform: "none", boxShadow: "none" }}
//                               className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
//                             >
//                               Edit <ChevronRight className="w-4 h-4 ml-1" />
//                             </button>
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               </div>
//             ) : (
//               /* Page Form View */
//               <div className="container mx-auto">
//                 <div className="bg-white shadow-sm rounded-lg overflow-hidden">
//                   <div className="p-6 border-b border-gray-200">
//                     <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
//                       <div className="flex items-center">
//                         <button
//                           style={{ transform: "none", boxShadow: "none" }}
//                           onClick={handleBackToList}
//                           className="mr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
//                         >
//                           <ArrowLeft className="w-5 h-5" />
//                         </button>
//                         <h1 className="text-2xl font-semibold text-gray-800">
//                           {currentPage
//                             ? `Edit: ${currentPage.title}`
//                             : "Create New Page"}
//                         </h1>
//                       </div>
//                       <div className="flex space-x-2 w-full sm:w-auto justify-end">
//                         {currentPage && (
//                           <>
//                             <button
//                               onClick={previewPage}
//                               className="bg-gray-100 font-normal hover:bg-gray-200 text-gray-800 px-3 py-1 rounded inline-flex items-center"
//                             >
//                               <Eye className="w-4 h-4 mr-1" /> Preview
//                             </button>
//                             <button
//                               onClick={deletePage}
//                               className="bg-red-500 font-normal hover:bg-red-600 text-white px-3 py-1 rounded inline-flex items-center"
//                             >
//                               <Trash2 className="w-4 h-4 mr-1" /> Delete
//                             </button>
//                           </>
//                         )}
//                         <button
//                           onClick={savePage}
//                           className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
//                         >
//                           <Save className="w-4 h-4 mr-1" /> Save
//                         </button>
//                       </div>
//                     </div>
//                   </div>

//                   {/* Tabs */}
//                   <div className="px-6 bg-white border-b border-gray-200 overflow-x-auto">
//                     <div className="flex whitespace-nowrap">
//                       <button
//                         className={`py-4 px-4 font-medium text-sm focus:outline-none transition-colors relative
//                         ${
//                           activeTab === "general"
//                             ? "text-blue-600 border-b-2 border-blue-600"
//                             : "text-gray-600 hover:text-gray-900"
//                         }
//                       `}
//                         onClick={() => setActiveTab("general")}
//                       >
//                         <div className="flex items-center">
//                           <FileText className="w-4 h-4 mr-2" />
//                           General
//                         </div>
//                       </button>
//                       <button
//                         className={`py-4 px-4 font-medium text-sm focus:outline-none transition-colors relative
//                         ${
//                           activeTab === "sections"
//                             ? "text-blue-600 border-b-2 border-blue-600"
//                             : "text-gray-600 hover:text-gray-900"
//                         }
//                       `}
//                         onClick={() => setActiveTab("sections")}
//                         disabled={!currentPage}
//                       >
//                         <div className="flex items-center">
//                           <Layers className="w-4 h-4 mr-3" />
//                           Sections
//                           {!currentPage && (
//                             <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
//                               Save first
//                             </span>
//                           )}
//                         </div>
//                       </button>
//                       <button
//                         className={`py-4 px-4 font-medium text-sm focus:outline-none transition-colors relative
//                         ${
//                           activeTab === "seo"
//                             ? "text-blue-600 border-b-2 border-blue-600"
//                             : "text-gray-600 hover:text-gray-900"
//                         }
//                       `}
//                         onClick={() => setActiveTab("seo")}
//                       >
//                         <div className="flex items-center">
//                           <Search className="w-4 h-4 mr-2" />
//                           SEO
//                         </div>
//                       </button>
//                     </div>
//                   </div>

//                   {/* Tab Content */}
//                   <div className="p-6">
//                     {activeTab === "general" && (
//                       <GeneralTab
//                         formData={formData}
//                         handleInputChange={handleInputChange}
//                         generateSlug={generateSlug}
//                         onSubmit={savePage}
//                         handleBackgroundUpdate={handleBackgroundUpdate}
//                       />
//                     )}

//                     {activeTab === "sections" && currentPage && (
//                       <SectionsTab
//                         page={currentPage}
//                         setPage={setCurrentPage}
//                         setError={setError}
//                         setLoading={setLoading}
//                       />
//                     )}

//                     {activeTab === "seo" && (
//                       <SeoTab
//                         formData={formData}
//                         handleInputChange={handleInputChange}
//                         onSubmit={savePage}
//                       />
//                     )}
//                   </div>
//                 </div>
//               </div>
//             )}
//           </>
//         )}
//       </div>

//       <style>{`
//         .btn-primary {
//           @apply bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors;
//         }

//         .btn-secondary {
//           @apply bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors;
//         }

//         .btn-danger {
//           @apply bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors;
//         }

//         input[type="text"],
//         input[type="email"],
//         input[type="password"],
//         textarea,
//         select {
//           @apply w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all;
//         }

//         input[type="checkbox"] {
//           @apply rounded text-blue-600 focus:ring-blue-500 h-4 w-4 transition-colors;
//         }

//         label {
//           @apply text-sm font-medium text-gray-700 mb-1 block;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default PageManagement;
